import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { WagonWheel, runColor } from "@/components/WagonWheel";
import { PitchMap } from "@/components/PitchMap";
import { Trophy, Dumbbell, Pencil, FileText } from "lucide-react";

function ZoneAnalytics({ shots }) {
  const t = useT();
  const zones = {};
  shots.forEach((s) => { const n = s.sector_name || `Sector ${s.sector}`; zones[n] = zones[n] || { runs: 0, balls: 0, boundaries: 0 }; zones[n].runs += s.runs || 0; zones[n].balls += 1; if ((s.runs || 0) >= 4) zones[n].boundaries += 1; });
  const rows = Object.entries(zones).sort((a, b) => b[1].runs - a[1].runs);
  if (!rows.length) return null;
  return (
    <div className="mt-4" data-testid="zone-analytics">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{t("Zone analytics")}</div>
      <div className="space-y-1.5">
        {rows.map(([name, z]) => (
          <div key={name} className="flex items-center gap-2 text-sm">
            <span className="w-28 text-slate-600 truncate">{name}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${Math.min(100, z.runs * 4)}%` }} /></div>
            <span className="font-mono font-bold w-20 text-right">{z.runs}r · SR {z.balls ? Math.round(z.runs / z.balls * 100) : 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EntryDetail() {
  const { id } = useParams();
  const t = useT();
  const [entry, setEntry] = useState(null);
  const [card, setCard] = useState(null);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get(`/entries/${id}`).then(({ data }) => setEntry(data));
    api.get(`/report-card/${id}`).then(({ data }) => setCard(data)).catch(() => {});
    api.get(`/coach-notes?entry_id=${id}`).then(({ data }) => setNotes(data)).catch(() => {});
  }, [id]);

  if (!entry) return <div className="max-w-3xl mx-auto space-y-3">{[1, 2].map((i) => <div key={i} className="skeleton h-40" />)}</div>;
  const isTour = entry.entry_type === "tournament";
  const d = entry.derived || {};

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <Link to="/app/entries" className="text-sm text-slate-500">← {t("Back")}</Link>
        <Link to={`/app/log/${id}`}><Button variant="outline" size="sm" data-testid="edit-entry-detail"><Pencil size={14} /> {t("Edit")}</Button></Link>
      </div>

      <div className="ct-card p-5" data-testid="entry-header">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white" style={{ background: isTour ? "#D90416" : "#1E293B" }}>{isTour ? <Trophy /> : <Dumbbell />}</div>
          <div>
            <h1 className="font-head font-bold text-2xl leading-tight">{entry.opponent || (isTour ? t("Match") : t("Practice"))}</h1>
            <p className="text-slate-500 text-sm">{entry.date} · {entry.tournament || entry.practice_type} {entry.format ? `· ${entry.format}` : ""}{entry.team ? ` · ${entry.team}` : ""}</p>
          </div>
        </div>
      </div>

      {entry.batting && (
        <div className="ct-card p-5" data-testid="batting-detail">
          <h2 className="font-head font-bold text-xl text-red-600 mb-3">{t("Batting")}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
            <Stat l={t("Runs")} v={entry.batting.runs} /><Stat l={t("Balls")} v={entry.batting.balls} />
            <Stat l="4s/6s" v={`${entry.batting.fours || 0}/${entry.batting.sixes || 0}`} />
            <Stat l="SR" v={d.batting?.strike_rate ?? "-"} /><Stat l="Bnd%" v={d.batting?.boundary_pct ?? "-"} />
            <Stat l={t("Out")} v={t(entry.batting.dismissal)} small />
          </div>
          {(entry.batting.shots || []).length > 0 && (
            <div className="flex flex-col items-center">
              <WagonWheel shots={entry.batting.shots} readOnly hand={entry.batting.batting_hand} />
              <ZoneAnalytics shots={entry.batting.shots} />
            </div>
          )}
          <NotesView section={entry.batting} />
        </div>
      )}

      {entry.bowling && (
        <div className="ct-card p-5" data-testid="bowling-detail">
          <h2 className="font-head font-bold text-xl text-blue-800 mb-3">{t("Bowling")}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
            <Stat l={t("Overs")} v={entry.bowling.overs} /><Stat l={t("Wkts")} v={entry.bowling.wickets} />
            <Stat l={t("Runs")} v={entry.bowling.runs_conceded} /><Stat l="Econ" v={d.bowling?.economy ?? "-"} />
            <Stat l={t("Maidens")} v={entry.bowling.maidens} /><Stat l="Dot%" v={d.bowling?.dot_pct ?? "-"} />
          </div>
          {(entry.bowling.pitch_map || []).length > 0 && <PitchMap cells={entry.bowling.pitch_map} readOnly />}
          <NotesView section={entry.bowling} />
        </div>
      )}

      {entry.fielding && (
        <div className="ct-card p-5"><h2 className="font-head font-bold text-xl text-amber-600 mb-3">{t("Fielding")}</h2>
          <div className="grid grid-cols-4 gap-3"><Stat l={t("Catches")} v={entry.fielding.catches} /><Stat l={t("Run-outs")} v={entry.fielding.runouts} /><Stat l={t("Stumpings")} v={entry.fielding.stumpings} /><Stat l={t("Drops")} v={entry.fielding.drops} /></div>
          <NotesView section={entry.fielding} />
        </div>
      )}

      {card && (
        <div className="ct-card p-5" data-testid="report-card">
          <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-brand" /><h2 className="font-head font-bold text-xl">{t("Report Card")}</h2></div>
          <p className="text-slate-700">{card.summary}</p>
          {card.comparisons?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {card.comparisons.map((c, i) => <div key={i} className="bg-slate-50 rounded-lg p-3 flex justify-between"><span className="text-sm text-slate-600">{c.metric}</span><span className="font-mono font-bold">{c.value ?? "-"} <span className="text-slate-400 text-xs">/ szn {c.season_avg ?? "-"}</span></span></div>)}
            </div>
          )}
        </div>
      )}

      {notes.length > 0 && (
        <div className="ct-card p-5" data-testid="entry-coach-notes">
          <h2 className="font-head font-bold text-xl mb-3">{t("Coach Notes")}</h2>
          {notes.map((n) => <div key={n.id} className="border-l-2 border-brand pl-3 py-1 mb-2"><p className="text-sm">{n.text}</p><p className="text-xs text-slate-400">{n.coach_name} · {new Date(n.created_at).toLocaleDateString()}</p></div>)}
        </div>
      )}
    </div>
  );
}

function Stat({ l, v, small }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{l}</div><div className={`stat-num tabular ${small ? "text-base" : "text-2xl"}`}>{v ?? "-"}</div></div>;
}

function NotesView({ section }) {
  const t = useT();
  if (!section || (!section.notes && !section.notes_audio)) return null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("ct_token") : "";
  return (
    <div className="mt-4 bg-slate-50 rounded-xl p-3" data-testid="section-match-notes">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">{t("Match notes")}</div>
      {section.notes && <p className="text-sm text-slate-700">{section.notes}</p>}
      {section.notes_audio && <audio controls src={`${backend}${section.notes_audio.url}?auth=${token}`} className="h-8 mt-2" />}
    </div>
  );
}
